import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileText, Search, Loader2, User as UserIcon } from 'lucide-react';
import { changeLogsAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function ChangeLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = collection === 'all' ? {} : { collection };
        const r = await changeLogsAPI.list(params);
        setLogs(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load change logs');
      } finally {
        setLoading(false);
      }
    })();
  }, [collection]);

  const filtered = logs.filter((l) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (l.field || '').toLowerCase().includes(q)
      || (l.reason || '').toLowerCase().includes(q)
      || (l.user_email || '').toLowerCase().includes(q)
      || (l.record_id || '').toLowerCase().includes(q)
    );
  });

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-6" data-testid="change-logs-page">
      <div>
        <h1 className="cp-page-title flex items-center gap-2">
          <FileText className="w-6 h-6 text-muted-foreground" />
          Change Management Log
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Audit trail of manual data edits. Each change requires a reason (ISO 14067 + CBAM audit compliance).
        </p>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search by field, reason, user, record ID"
                   className="pl-9 rtl:pl-3 rtl:pr-9" data-testid="change-logs-search" />
          </div>
          <Select value={collection} onValueChange={setCollection}>
            <SelectTrigger className="w-full sm:w-56" data-testid="change-logs-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All collections</SelectItem>
              <SelectItem value="products">Products</SelectItem>
              <SelectItem value="suppliers">Suppliers</SelectItem>
              <SelectItem value="invoices">Invoices</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="change-logs-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">When</th>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Collection</th>
                <th className="px-4 py-3 font-medium">Field</th>
                <th className="px-4 py-3 font-medium">Change</th>
                <th className="px-4 py-3 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-muted-foreground" data-testid="no-change-logs">
                  No change records yet. Edits with a reason will appear here.
                </td></tr>
              ) : filtered.map((l, i) => (
                <tr key={i} data-testid={`change-row-${i}`} className="hover:bg-muted/30">
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmt(l.timestamp)}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      <UserIcon className="w-3 h-3 text-muted-foreground" />
                      <span className="truncate max-w-[140px]">{l.user_email || '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{l.collection}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs">{l.field}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="text-muted-foreground line-through">{l.old_value || '—'}</span>
                    {' → '}
                    <span className="font-semibold">{l.new_value}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">{l.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
